import {Fragment, UUID} from '@nh/angular-tses';
import {WorkflowStates} from '../../workflow/workflow-states';
import {DASFragmentType} from './das-fragment-type';

export enum VirusScanState {
  PENDING = 'PENDING',
  OK = 'OK',
  VIRUS_DETECTED = 'VIRUS_DETECTED',
}

export enum UploadStatus {
  PENDING = 'PENDING',
  FAILED = 'FAILED',
}

export enum AttachmentType {
  SUBMISSION = 'SUBMISSION', // Attachment added during submission or rework
  SES_REVIEW = 'SES_REVIEW', // Attachment added during SES review
  DAS_ADMIN_ACTION = 'DAS_ADMIN_ACTION', // Attachment added by admin
  DAS_2 = 'DAS_2', // Attachment migrated from DAS 2
  POST_DETERMINATION = 'POST_DETERMINATION', // Attachment added post-determination (including withdrawn or rejected)
}

const attachmentTypeMapping: Readonly<Record<WorkflowStates, AttachmentType>> = Object.freeze({
  [WorkflowStates.NEED_IDENTIFIED]: AttachmentType.SUBMISSION,
  [WorkflowStates.SUBMISSION_IN_PREPARATION]: AttachmentType.SUBMISSION,
  [WorkflowStates.PM_APPRAISAL_REWORK]: AttachmentType.SUBMISSION,
  [WorkflowStates.DAS_ADMIN_REWORK]: AttachmentType.SUBMISSION,
  [WorkflowStates.SPECIALIST_SUBMISSION_POINT_REWORK]: AttachmentType.SUBMISSION,
  [WorkflowStates.REWORK_REQUIRED]: AttachmentType.SUBMISSION,

  [WorkflowStates.PM_APPRAISAL]: AttachmentType.DAS_ADMIN_ACTION,
  [WorkflowStates.DAS_ADMIN]: AttachmentType.DAS_ADMIN_ACTION,
  [WorkflowStates.PM_DETERMINATION]: AttachmentType.DAS_ADMIN_ACTION,

  [WorkflowStates.SPECIALIST_SUBMISSION_POINT]: AttachmentType.SES_REVIEW,
  [WorkflowStates.SPECIALIST_REVIEW]: AttachmentType.SES_REVIEW,

  [WorkflowStates.APPROVED]: AttachmentType.POST_DETERMINATION,
  [WorkflowStates.APPROVED_WITH_CONDITIONS]: AttachmentType.POST_DETERMINATION,
  [WorkflowStates.REJECTED]: AttachmentType.POST_DETERMINATION,
  [WorkflowStates.WITHDRAWN]: AttachmentType.POST_DETERMINATION,
});

/**
 * Get the correct attachment type for a given workflow state.
 *
 * DAS 2 states map to POST_DETERMINATION as they all DAS 2 departures are determined.
 *
 * @param workflowState the state.
 */
export function getAttachmentType(workflowState: string): AttachmentType {
  return attachmentTypeMapping[workflowState] || AttachmentType.POST_DETERMINATION;
}

/**
 * A fragment containing metadata about an attachment
 *
 * @field fileName       {string}    The filename of the attachment
 * @field fileId         {UUID}      The ID of the associated file upload
 * @field reference      {string}    A reference of the attachment
 * @field description    {string}    A description of the attachment
 * @field mimeType       {string}    The MIME type of the attachment
 * @field deleted        {boolean}   Whether the attachment is deleted, defaults to false
 */
export class AttachmentFragment extends Fragment {
  public deleted: boolean = false;

  /**
   * Static helper to create an Attachment Fragment.
   *
   * @param fileName    {string}                File name; defaults to empty string
   * @param fieldId     {UUID}                  File id; defaults to random uuid
   * @param reference   {string}                Fil reference; defaults to empty string
   * @param description {string}                File description; defaults to empty string
   * @param mimeType    {string}                Mime type; defaults to empty string
   * @param status      {FileStatus}            OPTIONAL! Status of the attachment upload;
   * @returns           {AttachmentFragment}    The Attachment Fragment object
   */

  public static empty(
    fileName: string = '',
    fileId: UUID = UUID.random(),
    reference: string = '',
    description: string = '',
    mimeType: string = '',
    attachmentType: AttachmentType = AttachmentType.SUBMISSION
  ): AttachmentFragment {
    return new AttachmentFragment(null, fileName, fileId, reference, description, mimeType, attachmentType);
  }

  constructor(
    id: UUID,
    fileName: string,
    public fileId: UUID,
    public reference: string,
    public description: string,
    public mimeType: string,
    public attachmentType: AttachmentType,
    public errorMessage: string = '',
    public virusScanState: VirusScanState = VirusScanState.PENDING,
    public status?: UploadStatus
  ) {
    super(id, DASFragmentType.ATTACHMENT, [], fileName);
  }

  /**
   * @inheritDoc
   */
  public split(offset: number): AttachmentFragment {
    return null;
  }
}

export function deserialiseAttachmentFragment(json: any): AttachmentFragment {
  const attachment: AttachmentFragment = new AttachmentFragment(
    UUID.orThrow(json.id),
    json.value,
    UUID.orThrow(json.fileId),
    json.reference,
    json.description,
    json.mimeType,
    json.attachmentType,
    '',
    json.virusScanState
  );

  attachment.deleted = json.deleted ? json.deleted : false;

  return attachment;
}
